import type { XLMCellAddress, XLMModel, XLMNode, XLMValue } from "../api/e2e";

type PeriodType = "fiscal_year" | "quarterly" | "half_year";
type PeriodKey = readonly [number] | readonly [number, number];
type Scalar = number | boolean | string;

interface FormulaCell {
  key: string;
  identifier: string;
  periodType: PeriodType;
  periodKey: PeriodKey;
  value: XLMValue;
}

type Expression =
  | { kind: "number"; value: number }
  | { kind: "text"; value: string }
  | { kind: "bare_ref"; identifier: string }
  | {
      kind: "indexed_ref";
      identifier: string;
      periodType: PeriodType;
      periodKey: PeriodKey;
    }
  | { kind: "call"; name: string; arguments: Expression[] }
  | {
      kind: "binary";
      operator: "+" | "-" | "*" | "/";
      left: Expression;
      right: Expression;
    }
  | {
      kind: "comparison";
      operator: "<=" | ">=" | "<>" | "=" | "<" | ">";
      left: Expression;
      right: Expression;
    }
  | { kind: "negative"; operand: Expression };

type TokenKind =
  | "identifier"
  | "number"
  | "text"
  | "period"
  | "left_parenthesis"
  | "right_parenthesis"
  | "plus"
  | "minus"
  | "star"
  | "slash"
  | "comma"
  | "comparison"
  | "end";

interface Token {
  kind: TokenKind;
  text: string;
  position: number;
}

interface FormulaProgram {
  expressions: Map<string, Expression | null>;
  dependencies: Map<string, Set<string>>;
  formulaDependencies: Map<string, Set<string>>;
  reverseDependencies: Map<string, string[]>;
  ordered: string[];
  cycles: Set<string>;
}

interface FormulaCells {
  cells: FormulaCell[];
  index: Map<string, FormulaCell>;
  knownIdentifiers: Set<string>;
  signature: string | null;
}

interface EvaluationResult {
  ok: boolean;
  value?: Scalar;
}

export interface XLMFormulaRecalculation {
  computed: number;
  errors: number;
}

export interface XLMFormulaValueUpdate {
  cell: XLMCellAddress;
  computedValue: number | boolean | null;
}

export interface XLMFormulaCalculation {
  recalculation: XLMFormulaRecalculation;
  updates: XLMFormulaValueUpdate[];
}

const PARSE_CACHE_SIZE = 2_048;
const MAX_CACHED_FORMULA_LENGTH = 512;
const MAX_FORMULA_LENGTH = 4_096;
const MAX_FORMULA_NESTING = 128;
const PROGRAM_CACHE_SIZE = 8;
const MAX_CACHED_PROGRAM_CHARS = 2_000_000;

const parseCache = new Map<string, Expression | null>();
const programCache = new Map<string, FormulaProgram>();
const modelProgramCache = new WeakMap<XLMModel, FormulaProgram>();

class FormulaError extends Error {}

function isIdentifierStart(character: string): boolean {
  return /[A-Za-z_]/u.test(character);
}

function isIdentifierPart(character: string): boolean {
  return /[A-Za-z0-9_]/u.test(character);
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;
  while (index < source.length) {
    const character = source[index];
    if (character === undefined) break;
    if (/\s/u.test(character)) {
      index += 1;
      continue;
    }
    if (isIdentifierStart(character)) {
      let end = index + 1;
      while (
        end < source.length &&
        source[end] !== undefined &&
        isIdentifierPart(source[end] ?? "")
      ) {
        end += 1;
      }
      tokens.push({
        kind: "identifier",
        text: source.slice(index, end),
        position: index,
      });
      index = end;
      continue;
    }
    const number = source
      .slice(index)
      .match(/^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/u)?.[0];
    if (number) {
      tokens.push({ kind: "number", text: number, position: index });
      index += number.length;
      continue;
    }
    if (character === '"') {
      const start = index;
      index += 1;
      let text = "";
      let terminated = false;
      while (index < source.length) {
        const next = source[index];
        if (next !== '"') {
          text += next ?? "";
          index += 1;
          continue;
        }
        if (source[index + 1] === '"') {
          text += '"';
          index += 2;
          continue;
        }
        index += 1;
        terminated = true;
        break;
      }
      if (!terminated)
        throw new FormulaError(`Unterminated string at ${start}.`);
      tokens.push({ kind: "text", text, position: start });
      continue;
    }
    if (character === "[") {
      const end = source.indexOf("]", index + 1);
      if (end < 0) throw new FormulaError(`Unterminated period at ${index}.`);
      tokens.push({
        kind: "period",
        text: source.slice(index + 1, end),
        position: index,
      });
      index = end + 1;
      continue;
    }
    const comparison = ["<=", ">=", "<>", "=", "<", ">"].find((item) =>
      source.startsWith(item, index),
    );
    if (comparison) {
      tokens.push({
        kind: "comparison",
        text: comparison,
        position: index,
      });
      index += comparison.length;
      continue;
    }
    const singleCharacters: Record<string, TokenKind> = {
      "(": "left_parenthesis",
      ")": "right_parenthesis",
      "+": "plus",
      "-": "minus",
      "*": "star",
      "/": "slash",
      ",": "comma",
    };
    const kind = singleCharacters[character];
    if (!kind) {
      throw new FormulaError(
        `Unexpected character ${JSON.stringify(character)} at ${index}.`,
      );
    }
    tokens.push({ kind, text: character, position: index });
    index += 1;
  }
  tokens.push({ kind: "end", text: "", position: source.length });
  return tokens;
}

function parsePeriod(token: Token): {
  periodType: PeriodType;
  periodKey: PeriodKey;
} {
  const quarterly = token.text.match(/^(\d{4})Q(\d+)$/u);
  if (quarterly) {
    return {
      periodType: "quarterly",
      periodKey: [Number(quarterly[1]), Number(quarterly[2])],
    };
  }
  const halfYear = token.text.match(/^(\d{4})H(\d+)$/u);
  if (halfYear) {
    return {
      periodType: "half_year",
      periodKey: [Number(halfYear[1]), Number(halfYear[2])],
    };
  }
  if (/^\d{4}$/u.test(token.text)) {
    return { periodType: "fiscal_year", periodKey: [Number(token.text)] };
  }
  throw new FormulaError(
    `Invalid period [${token.text}] at ${token.position}.`,
  );
}

class FormulaParser {
  private position = 0;
  private nesting = 0;

  constructor(private readonly tokens: Token[]) {}

  parse(): Expression {
    const expression = this.parseComparison();
    this.eat("end");
    return expression;
  }

  private peek(): Token {
    const token = this.tokens[this.position];
    if (!token) throw new FormulaError("Formula ended unexpectedly.");
    return token;
  }

  private eat(kind: TokenKind): Token {
    const token = this.peek();
    if (token.kind !== kind) {
      throw new FormulaError(
        `Expected ${kind} at ${token.position}, got ${token.kind}.`,
      );
    }
    this.position += 1;
    return token;
  }

  private nested(parse: () => Expression): Expression {
    if (this.nesting >= MAX_FORMULA_NESTING) {
      throw new FormulaError("Formula nesting limit exceeded.");
    }
    this.nesting += 1;
    try {
      return parse();
    } finally {
      this.nesting -= 1;
    }
  }

  private parseComparison(): Expression {
    const left = this.parseExpression();
    if (this.peek().kind !== "comparison") return left;
    const operator = this.eat("comparison").text;
    if (!isComparisonOperator(operator)) {
      throw new FormulaError(`Unsupported comparison ${operator}.`);
    }
    return {
      kind: "comparison",
      operator,
      left,
      right: this.parseExpression(),
    };
  }

  private parseExpression(): Expression {
    let left = this.parseTerm();
    while (this.peek().kind === "plus" || this.peek().kind === "minus") {
      const operator = this.eat(this.peek().kind).text;
      const right = this.parseTerm();
      left = {
        kind: "binary",
        operator: operator === "+" ? "+" : "-",
        left,
        right,
      };
    }
    return left;
  }

  private parseTerm(): Expression {
    let left = this.parseFactor();
    while (this.peek().kind === "star" || this.peek().kind === "slash") {
      const operator = this.eat(this.peek().kind).text;
      const right = this.parseFactor();
      left = {
        kind: "binary",
        operator: operator === "*" ? "*" : "/",
        left,
        right,
      };
    }
    return left;
  }

  private parseFactor(): Expression {
    if (this.peek().kind === "minus") {
      this.eat("minus");
      return {
        kind: "negative",
        operand: this.nested(() => this.parseFactor()),
      };
    }
    if (this.peek().kind === "plus") {
      this.eat("plus");
      return this.nested(() => this.parseFactor());
    }
    return this.parseAtom();
  }

  private parseAtom(): Expression {
    const token = this.peek();
    if (token.kind === "number") {
      this.eat("number");
      const value = Number(token.text);
      if (!Number.isFinite(value)) throw new FormulaError("Invalid number.");
      return { kind: "number", value };
    }
    if (token.kind === "text") {
      this.eat("text");
      return { kind: "text", value: token.text };
    }
    if (token.kind === "left_parenthesis") {
      this.eat("left_parenthesis");
      const expression = this.nested(() => this.parseComparison());
      this.eat("right_parenthesis");
      return expression;
    }
    if (token.kind !== "identifier") {
      throw new FormulaError(`Unexpected ${token.kind} at ${token.position}.`);
    }
    this.eat("identifier");
    if (this.peek().kind === "left_parenthesis") {
      this.eat("left_parenthesis");
      const args: Expression[] = [];
      if (this.peek().kind !== "right_parenthesis") {
        args.push(this.nested(() => this.parseComparison()));
        while (this.peek().kind === "comma") {
          this.eat("comma");
          args.push(this.nested(() => this.parseComparison()));
        }
      }
      this.eat("right_parenthesis");
      return { kind: "call", name: token.text.toUpperCase(), arguments: args };
    }
    if (this.peek().kind === "period") {
      const period = parsePeriod(this.eat("period"));
      return {
        kind: "indexed_ref",
        identifier: token.text,
        ...period,
      };
    }
    return { kind: "bare_ref", identifier: token.text };
  }
}

function isComparisonOperator(
  operator: string,
): operator is "<=" | ">=" | "<>" | "=" | "<" | ">" {
  return ["<=", ">=", "<>", "=", "<", ">"].includes(operator);
}

function parseFormula(formula: string): Expression | null {
  const body = formula.startsWith("=") ? formula.slice(1) : formula;
  if (body.length > MAX_FORMULA_LENGTH) return null;
  if (body.length <= MAX_CACHED_FORMULA_LENGTH && parseCache.has(body)) {
    return parseCache.get(body) ?? null;
  }
  let parsed: Expression | null = null;
  try {
    parsed = new FormulaParser(tokenize(body)).parse();
  } catch (error) {
    if (!(error instanceof FormulaError)) throw error;
  }
  if (body.length <= MAX_CACHED_FORMULA_LENGTH) {
    parseCache.set(body, parsed);
    while (parseCache.size > PARSE_CACHE_SIZE) {
      const oldest = parseCache.keys().next().value;
      if (typeof oldest !== "string") break;
      parseCache.delete(oldest);
    }
  }
  return parsed;
}

function periodKeyText(periodKey: PeriodKey): string {
  return periodKey.join(":");
}

function formulaCellKey(
  identifier: string,
  periodType: PeriodType,
  periodKey: PeriodKey,
): string {
  return `${identifier}\u001f${periodType}\u001f${periodKeyText(periodKey)}`;
}

function appendNodeCells(
  node: XLMNode,
  cells: FormulaCell[],
  knownIdentifiers: Set<string>,
): void {
  const hasCells = Boolean(
    node.yearly_values?.length ||
    node.quarterly_values?.length ||
    node.half_year_values?.length,
  );
  if (hasCells) knownIdentifiers.add(node.identifier);
  for (const value of node.yearly_values ?? []) {
    cells.push({
      key: formulaCellKey(node.identifier, "fiscal_year", [value.year]),
      identifier: node.identifier,
      periodType: "fiscal_year",
      periodKey: [value.year],
      value,
    });
  }
  for (const value of node.quarterly_values ?? []) {
    const periodKey: PeriodKey = [value.year, value.quarter ?? 0];
    cells.push({
      key: formulaCellKey(node.identifier, "quarterly", periodKey),
      identifier: node.identifier,
      periodType: "quarterly",
      periodKey,
      value,
    });
  }
  for (const value of node.half_year_values ?? []) {
    const periodKey: PeriodKey = [value.year, value.half ?? 0];
    cells.push({
      key: formulaCellKey(node.identifier, "half_year", periodKey),
      identifier: node.identifier,
      periodType: "half_year",
      periodKey,
      value,
    });
  }
  for (const item of node.breakdown?.items ?? []) {
    appendNodeCells(item, cells, knownIdentifiers);
  }
}

function formulaCells(model: XLMModel, includeSignature = true): FormulaCells {
  const cells: FormulaCell[] = [];
  const knownIdentifiers = new Set<string>();
  for (const node of model.nodes) {
    appendNodeCells(node, cells, knownIdentifiers);
  }
  const index = new Map(cells.map((cell) => [cell.key, cell]));
  return {
    cells,
    index,
    knownIdentifiers,
    signature: includeSignature
      ? [
          ...[...knownIdentifiers].map((identifier) => `i:${identifier}`),
          ...cells.map((cell) => `c:${cell.key}:${cell.value.formula ?? ""}`),
        ].join("\u001e")
      : null,
  };
}

function collectDependencies(
  expression: Expression,
  periodType: PeriodType,
  periodKey: PeriodKey,
  dependencies: Set<string>,
): void {
  if (expression.kind === "number" || expression.kind === "text") return;
  if (expression.kind === "bare_ref") {
    dependencies.add(
      formulaCellKey(expression.identifier, periodType, periodKey),
    );
    return;
  }
  if (expression.kind === "indexed_ref") {
    dependencies.add(
      formulaCellKey(
        expression.identifier,
        expression.periodType,
        expression.periodKey,
      ),
    );
    return;
  }
  if (expression.kind === "negative") {
    collectDependencies(
      expression.operand,
      periodType,
      periodKey,
      dependencies,
    );
    return;
  }
  if (expression.kind === "binary" || expression.kind === "comparison") {
    collectDependencies(expression.left, periodType, periodKey, dependencies);
    collectDependencies(expression.right, periodType, periodKey, dependencies);
    return;
  }
  for (const argument of expression.arguments) {
    collectDependencies(argument, periodType, periodKey, dependencies);
  }
}

function formulaTopology(
  expressions: ReadonlyMap<string, Expression | null>,
  formulaDependencies: ReadonlyMap<string, ReadonlySet<string>>,
  reverseDependencies: ReadonlyMap<string, readonly string[]>,
): { ordered: string[]; cycles: Set<string> } {
  const indegree = new Map(
    [...expressions.keys()].map((key) => [
      key,
      formulaDependencies.get(key)?.size ?? 0,
    ]),
  );
  const queue = [...indegree]
    .filter(([, degree]) => degree === 0)
    .map(([key]) => key);
  const ordered: string[] = [];
  for (let position = 0; position < queue.length; position += 1) {
    const key = queue[position];
    if (!key) continue;
    ordered.push(key);
    for (const dependent of reverseDependencies.get(key) ?? []) {
      const degree = (indegree.get(dependent) ?? 0) - 1;
      indegree.set(dependent, degree);
      if (degree === 0) queue.push(dependent);
    }
  }
  return {
    ordered,
    cycles: new Set(
      [...indegree].filter(([, degree]) => degree > 0).map(([key]) => key),
    ),
  };
}

function buildFormulaProgram(cells: FormulaCell[]): FormulaProgram {
  const expressions = new Map<string, Expression | null>();
  for (const cell of cells) {
    if (cell.value.formula !== null && cell.value.formula !== undefined) {
      expressions.set(cell.key, parseFormula(cell.value.formula));
    }
  }
  const formulaKeys = new Set(expressions.keys());
  const dependencies = new Map<string, Set<string>>();
  const formulaDependencies = new Map<string, Set<string>>();
  const reverseDependencies = new Map<string, string[]>();
  for (const cell of cells) {
    const expression = expressions.get(cell.key);
    if (expression === undefined) continue;
    const raw = new Set<string>();
    if (expression) {
      collectDependencies(expression, cell.periodType, cell.periodKey, raw);
    }
    dependencies.set(cell.key, raw);
    const cellFormulaDependencies = new Set(
      [...raw].filter((key) => formulaKeys.has(key)),
    );
    formulaDependencies.set(cell.key, cellFormulaDependencies);
    for (const dependency of raw) {
      reverseDependencies.set(dependency, [
        ...(reverseDependencies.get(dependency) ?? []),
        cell.key,
      ]);
    }
  }
  const topology = formulaTopology(
    expressions,
    formulaDependencies,
    reverseDependencies,
  );
  return {
    expressions,
    dependencies,
    formulaDependencies,
    reverseDependencies,
    ...topology,
  };
}

function formulaProgram(cells: FormulaCells): FormulaProgram {
  const signature = cells.signature;
  if (
    signature !== null &&
    signature.length <= MAX_CACHED_PROGRAM_CHARS &&
    programCache.has(signature)
  ) {
    const cached = programCache.get(signature);
    if (cached) return cached;
  }
  const program = buildFormulaProgram(cells.cells);
  if (signature !== null && signature.length <= MAX_CACHED_PROGRAM_CHARS) {
    programCache.set(signature, program);
    while (programCache.size > PROGRAM_CACHE_SIZE) {
      const oldest = programCache.keys().next().value;
      if (typeof oldest !== "string") break;
      programCache.delete(oldest);
    }
  }
  return program;
}

function formulaEvaluation(model: XLMModel): {
  cells: FormulaCells;
  program: FormulaProgram;
} {
  const cached = modelProgramCache.get(model);
  const cells = formulaCells(model, cached === undefined);
  const program = cached ?? formulaProgram(cells);
  if (!cached) modelProgramCache.set(model, program);
  return { cells, program };
}

export function prepareXLMFormulaEvaluation(model: XLMModel): void {
  formulaEvaluation(model);
}

function numeric(value: Scalar): number {
  if (typeof value === "string") throw new FormulaError("Expected a number.");
  return typeof value === "boolean" ? Number(value) : value;
}

function finite(value: number): number {
  if (!Number.isFinite(value)) throw new FormulaError("Non-finite result.");
  return value;
}

function sum(values: number[]): number {
  let total = 0;
  let compensation = 0;
  for (const value of values) {
    const corrected = value - compensation;
    const next = total + corrected;
    compensation = next - total - corrected;
    total = next;
  }
  return finite(total);
}

function rounded(value: number, digits: number): number {
  const shift = (candidate: number, places: number): number => {
    const [coefficient, rawExponent = "0"] = String(candidate).split("e");
    const exponent = Number(rawExponent) + places;
    return Number(`${coefficient}e${exponent}`);
  };
  const shifted = shift(Math.abs(value), digits);
  if (!Number.isFinite(shifted)) {
    throw new FormulaError("Invalid rounding precision.");
  }
  return finite(Math.sign(value) * shift(Math.floor(shifted + 0.5), -digits));
}

function compare(
  operator: "<=" | ">=" | "<>" | "=" | "<" | ">",
  left: Scalar,
  right: Scalar,
): boolean {
  if (typeof left === "string" || typeof right === "string") {
    if (typeof left !== "string" || typeof right !== "string") {
      throw new FormulaError("Comparison operands have different types.");
    }
    if (operator === "<=") return left <= right;
    if (operator === ">=") return left >= right;
    if (operator === "<>") return left !== right;
    if (operator === "=") return left === right;
    if (operator === "<") return left < right;
    return left > right;
  }
  const numericLeft = numeric(left);
  const numericRight = numeric(right);
  if (operator === "<=") return numericLeft <= numericRight;
  if (operator === ">=") return numericLeft >= numericRight;
  if (operator === "<>") return numericLeft !== numericRight;
  if (operator === "=") return numericLeft === numericRight;
  if (operator === "<") return numericLeft < numericRight;
  return numericLeft > numericRight;
}

function evaluateCall(
  expression: Extract<Expression, { kind: "call" }>,
  evaluate: (argument: Expression) => Scalar,
): Scalar {
  const numbers = () =>
    expression.arguments.map((argument) => numeric(evaluate(argument)));
  if (expression.name === "SUM") return sum(numbers());
  if (expression.name === "ABS") {
    if (expression.arguments.length !== 1) throw new FormulaError("ABS arity.");
    const argument = expression.arguments[0];
    if (!argument) throw new FormulaError("ABS argument is missing.");
    return Math.abs(numeric(evaluate(argument)));
  }
  if (expression.name === "MAX" || expression.name === "MIN") {
    const values = numbers();
    if (values.length === 0)
      throw new FormulaError(`${expression.name} arity.`);
    return expression.name === "MAX"
      ? Math.max(...values)
      : Math.min(...values);
  }
  if (expression.name === "AVERAGE") {
    const values = numbers();
    if (values.length === 0) throw new FormulaError("AVERAGE arity.");
    return sum(values) / values.length;
  }
  if (expression.name === "POWER") {
    if (expression.arguments.length !== 2)
      throw new FormulaError("POWER arity.");
    const base = expression.arguments[0];
    const exponent = expression.arguments[1];
    if (!base || !exponent)
      throw new FormulaError("POWER argument is missing.");
    return finite(
      Math.pow(numeric(evaluate(base)), numeric(evaluate(exponent))),
    );
  }
  if (expression.name === "COUNT" || expression.name === "COUNTA") {
    return expression.arguments.length;
  }
  if (
    expression.name === "ROUND" ||
    expression.name === "ROUNDUP" ||
    expression.name === "ROUNDDOWN"
  ) {
    if (expression.arguments.length !== 2) {
      throw new FormulaError(`${expression.name} arity.`);
    }
    const valueExpression = expression.arguments[0];
    const digitsExpression = expression.arguments[1];
    if (!valueExpression || !digitsExpression) {
      throw new FormulaError(`${expression.name} argument is missing.`);
    }
    const value = numeric(evaluate(valueExpression));
    const digits = Math.trunc(numeric(evaluate(digitsExpression)));
    if (expression.name === "ROUND") return rounded(value, digits);
    const factor = 10 ** digits;
    if (!Number.isFinite(factor) || factor === 0) {
      throw new FormulaError("Invalid rounding precision.");
    }
    const magnitude = Math.abs(value) * factor;
    const result =
      expression.name === "ROUNDUP"
        ? Math.ceil(magnitude)
        : Math.floor(magnitude);
    return finite((Math.sign(value) * result) / factor);
  }
  if (expression.name === "IF") {
    if (expression.arguments.length !== 3) throw new FormulaError("IF arity.");
    const conditionExpression = expression.arguments[0];
    const truthyExpression = expression.arguments[1];
    const falseyExpression = expression.arguments[2];
    if (!conditionExpression || !truthyExpression || !falseyExpression) {
      throw new FormulaError("IF argument is missing.");
    }
    const condition = evaluate(conditionExpression);
    if (typeof condition === "string") {
      throw new FormulaError("IF condition must be numeric or Boolean.");
    }
    return (typeof condition === "boolean" ? condition : condition !== 0)
      ? evaluate(truthyExpression)
      : evaluate(falseyExpression);
  }
  if (expression.name === "IFERROR" || expression.name === "IFNA") {
    if (expression.arguments.length !== 2) {
      throw new FormulaError(`${expression.name} arity.`);
    }
    const primary = expression.arguments[0];
    const fallback = expression.arguments[1];
    if (!primary || !fallback) {
      throw new FormulaError(`${expression.name} argument is missing.`);
    }
    try {
      return evaluate(primary);
    } catch (error) {
      if (!(error instanceof FormulaError)) throw error;
      return evaluate(fallback);
    }
  }
  if (expression.name === "TRUE") return true;
  if (expression.name === "FALSE") return false;
  throw new FormulaError(`Unsupported function ${expression.name}.`);
}

function evaluateExpression(
  expression: Expression,
  periodType: PeriodType,
  periodKey: PeriodKey,
  lookup: (
    identifier: string,
    referencedPeriodType: PeriodType,
    referencedPeriodKey: PeriodKey,
  ) => Scalar,
): Scalar {
  const evaluate = (candidate: Expression): Scalar =>
    evaluateExpression(candidate, periodType, periodKey, lookup);
  if (expression.kind === "number" || expression.kind === "text") {
    return expression.value;
  }
  if (expression.kind === "bare_ref") {
    return lookup(expression.identifier, periodType, periodKey);
  }
  if (expression.kind === "indexed_ref") {
    return lookup(
      expression.identifier,
      expression.periodType,
      expression.periodKey,
    );
  }
  if (expression.kind === "negative") {
    return -numeric(evaluate(expression.operand));
  }
  if (expression.kind === "binary") {
    const left = numeric(evaluate(expression.left));
    const right = numeric(evaluate(expression.right));
    if (expression.operator === "+") return finite(left + right);
    if (expression.operator === "-") return finite(left - right);
    if (expression.operator === "*") return finite(left * right);
    if (right === 0) throw new FormulaError("Division by zero.");
    return finite(left / right);
  }
  if (expression.kind === "comparison") {
    return compare(
      expression.operator,
      evaluate(expression.left),
      evaluate(expression.right),
    );
  }
  return evaluateCall(expression, evaluate);
}

const failedEvaluation: EvaluationResult = { ok: false };

function addressedCellKey(cell: XLMCellAddress): string | null {
  if (cell.collection === "yearly") {
    return formulaCellKey(cell.identifier, "fiscal_year", [cell.year]);
  }
  if (cell.collection === "quarterly" && cell.index !== null) {
    return formulaCellKey(cell.identifier, "quarterly", [
      cell.year,
      cell.index,
    ]);
  }
  if (cell.collection === "half_year" && cell.index !== null) {
    return formulaCellKey(cell.identifier, "half_year", [
      cell.year,
      cell.index,
    ]);
  }
  return null;
}

function formulaCellAddress(cell: FormulaCell): XLMCellAddress {
  if (cell.periodType === "fiscal_year") {
    return {
      identifier: cell.identifier,
      collection: "yearly",
      year: cell.periodKey[0],
      index: null,
    };
  }
  return {
    identifier: cell.identifier,
    collection: cell.periodType === "quarterly" ? "quarterly" : "half_year",
    year: cell.periodKey[0],
    index: cell.periodKey[1] ?? null,
  };
}

function addressedPeriod(cell: XLMCellAddress): {
  periodType: PeriodType;
  periodKey: PeriodKey;
} | null {
  if (cell.collection === "yearly") {
    return { periodType: "fiscal_year", periodKey: [cell.year] };
  }
  if (cell.collection === "quarterly" && cell.index !== null) {
    return {
      periodType: "quarterly",
      periodKey: [cell.year, cell.index],
    };
  }
  if (cell.collection === "half_year" && cell.index !== null) {
    return {
      periodType: "half_year",
      periodKey: [cell.year, cell.index],
    };
  }
  return null;
}

function equalDependencies(
  left: ReadonlySet<string>,
  right: ReadonlySet<string>,
): boolean {
  return left.size === right.size && [...left].every((item) => right.has(item));
}

/** Carry the immutable formula program across a structurally shared cell
 * patch. A formula edit reparses and rewires only that cell; value and
 * annotation patches reuse the complete dependency graph. */
export function carryXLMFormulaEvaluation(
  source: XLMModel,
  target: XLMModel,
  changed?: { cell: XLMCellAddress; formula: string | null },
): void {
  const sourceProgram =
    modelProgramCache.get(source) ?? formulaEvaluation(source).program;
  if (!changed) {
    modelProgramCache.set(target, sourceProgram);
    return;
  }
  const key = addressedCellKey(changed.cell);
  const period = addressedPeriod(changed.cell);
  if (!key || !period) {
    modelProgramCache.set(target, sourceProgram);
    return;
  }

  const hadFormula = sourceProgram.expressions.has(key);
  const hasFormula = changed.formula !== null;
  const expression = hasFormula ? parseFormula(changed.formula ?? "") : null;
  const nextDependencies = new Set<string>();
  if (expression) {
    collectDependencies(
      expression,
      period.periodType,
      period.periodKey,
      nextDependencies,
    );
  }
  const previousDependencies =
    sourceProgram.dependencies.get(key) ?? new Set<string>();
  const sameGraph =
    hadFormula === hasFormula &&
    equalDependencies(previousDependencies, nextDependencies);
  const expressions = new Map(sourceProgram.expressions);
  if (hasFormula) expressions.set(key, expression);
  else expressions.delete(key);
  if (sameGraph) {
    modelProgramCache.set(target, { ...sourceProgram, expressions });
    return;
  }

  const dependencies = new Map(sourceProgram.dependencies);
  if (hasFormula) dependencies.set(key, nextDependencies);
  else dependencies.delete(key);
  const reverseDependencies = new Map(sourceProgram.reverseDependencies);
  for (const dependency of previousDependencies) {
    const dependents = (reverseDependencies.get(dependency) ?? []).filter(
      (dependent) => dependent !== key,
    );
    if (dependents.length > 0) reverseDependencies.set(dependency, dependents);
    else reverseDependencies.delete(dependency);
  }
  for (const dependency of nextDependencies) {
    const dependents = reverseDependencies.get(dependency) ?? [];
    if (!dependents.includes(key)) {
      reverseDependencies.set(dependency, [...dependents, key]);
    }
  }
  const formulaKeys = new Set(expressions.keys());
  const formulaDependencies = new Map(
    [...dependencies].map(([formulaKey, rawDependencies]) => [
      formulaKey,
      new Set([...rawDependencies].filter((item) => formulaKeys.has(item))),
    ]),
  );
  const topology = formulaTopology(
    expressions,
    formulaDependencies,
    reverseDependencies,
  );
  modelProgramCache.set(target, {
    expressions,
    dependencies,
    formulaDependencies,
    reverseDependencies,
    ...topology,
  });
}

function selectedFormulaKeys(
  program: FormulaProgram,
  changedCell: XLMCellAddress | undefined,
): { affected: Set<string>; required: Set<string> } {
  if (!changedCell) {
    const all = new Set(program.expressions.keys());
    return { affected: all, required: new Set(all) };
  }
  const changedKey = addressedCellKey(changedCell);
  if (!changedKey) return { affected: new Set(), required: new Set() };

  const affected = new Set<string>();
  const downstream = [changedKey];
  if (program.expressions.has(changedKey)) affected.add(changedKey);
  for (let index = 0; index < downstream.length; index += 1) {
    const key = downstream[index];
    if (!key) continue;
    for (const dependent of program.reverseDependencies.get(key) ?? []) {
      if (affected.has(dependent)) continue;
      affected.add(dependent);
      downstream.push(dependent);
    }
  }

  const required = new Set(affected);
  const upstream = [...affected];
  for (let index = 0; index < upstream.length; index += 1) {
    const key = upstream[index];
    if (!key) continue;
    for (const dependency of program.formulaDependencies.get(key) ?? []) {
      if (required.has(dependency)) continue;
      required.add(dependency);
      upstream.push(dependency);
    }
  }
  return { affected, required };
}

/**
 * Recalculate the browser's provisional formula values with the same bounded
 * language and missing-cell semantics as the backend XLM compiler. The API's
 * returned patches remain authoritative and replace every provisional value.
 */
export function calculateXLMFormulaValues(
  model: XLMModel,
  changedCell?: XLMCellAddress,
): XLMFormulaCalculation {
  const { cells, program } = formulaEvaluation(model);
  const { affected, required } = selectedFormulaKeys(program, changedCell);
  if (affected.size === 0) {
    return { recalculation: { computed: 0, errors: 0 }, updates: [] };
  }
  const results = new Map<string, EvaluationResult>();
  for (const key of program.cycles) {
    if (required.has(key)) results.set(key, failedEvaluation);
  }

  const lookup = (
    identifier: string,
    periodType: PeriodType,
    periodKey: PeriodKey,
  ): Scalar => {
    if (!cells.knownIdentifiers.has(identifier)) {
      throw new FormulaError(`Unknown identifier ${identifier}.`);
    }
    const cell = cells.index.get(
      formulaCellKey(identifier, periodType, periodKey),
    );
    if (!cell) return 0;
    if (cell.value.formula !== null && cell.value.formula !== undefined) {
      const result = results.get(cell.key);
      if (result?.ok && result.value !== undefined) {
        return result.value === "" ? 0 : result.value;
      }
      if (
        !required.has(cell.key) &&
        (typeof cell.value.computed_value === "number" ||
          typeof cell.value.computed_value === "boolean")
      ) {
        return cell.value.computed_value;
      }
      if (!result?.ok || result.value === undefined) {
        throw new FormulaError(`Unavailable formula ${identifier}.`);
      }
    }
    return cell.value.value ?? 0;
  };

  let computed = 0;
  let errors = [...program.cycles].filter((key) => affected.has(key)).length;
  for (const key of program.ordered) {
    if (!required.has(key)) continue;
    const cell = cells.index.get(key);
    const expression = program.expressions.get(key);
    if (!cell || !expression) {
      results.set(key, failedEvaluation);
      if (affected.has(key)) errors += 1;
      continue;
    }
    try {
      const value = evaluateExpression(
        expression,
        cell.periodType,
        cell.periodKey,
        lookup,
      );
      if (typeof value === "number") finite(value);
      results.set(key, { ok: true, value });
      if (affected.has(key)) computed += 1;
    } catch (error) {
      if (!(error instanceof FormulaError)) throw error;
      results.set(key, failedEvaluation);
      if (affected.has(key)) errors += 1;
    }
  }
  const updates = [...affected].flatMap((key) => {
    const cell = cells.index.get(key);
    if (!cell) return [];
    const result = results.get(key);
    return [
      {
        cell: formulaCellAddress(cell),
        computedValue:
          result?.ok &&
          (typeof result.value === "number" ||
            typeof result.value === "boolean")
            ? result.value
            : null,
      },
    ];
  });
  return { recalculation: { computed, errors }, updates };
}

export function recalculateXLMFormulas(
  model: XLMModel,
  changedCell?: XLMCellAddress,
): XLMFormulaRecalculation {
  // This compatibility API intentionally mutates `model`; discard a prior
  // program because callers may also have changed formulas in place.
  modelProgramCache.delete(model);
  const calculation = calculateXLMFormulaValues(model, changedCell);
  const cells = formulaCells(model, false);
  for (const update of calculation.updates) {
    const key = addressedCellKey(update.cell);
    const cell = key ? cells.index.get(key) : undefined;
    if (cell) cell.value.computed_value = update.computedValue;
  }
  return calculation.recalculation;
}
